import { useTranslation } from '@topolo-io/ui-kit';


/**
 * The loading state for the guest project view.
 *
 * It deliberately mirrors `pages/projects/guest/[token].tsx` block for block —
 * same header bar, same container widths, same four-card grid, same section
 * heights. The generic content skeleton that used to stand in here was a
 * narrow centred column, so the page visibly jumped to a full-width layout the
 * moment the share resolved.
 */

/** Left label width, then where the bar starts and how long it runs. */
const TIMELINE_BARS = [
  { labelWidthClass: 'w-56', offset: '0%', width: '62%' },
  { labelWidthClass: 'w-48', offset: '8%', width: '44%' },
  { labelWidthClass: 'w-52', offset: '18%', width: '38%' },
  { labelWidthClass: 'w-44', offset: '12%', width: '55%' },
  { labelWidthClass: 'w-56', offset: '34%', width: '40%' },
  { labelWidthClass: 'w-40', offset: '48%', width: '30%' },
];

function Block({ className }: { className: string }) {
  return <div className={`rounded bg-muted ${className}`} />;
}

export function GuestProjectSkeleton() {
  const { t } = useTranslation();

  return (
    <div
      className="min-h-screen animate-pulse bg-background text-foreground"
      role="status"
      aria-live="polite"
      aria-busy="true"
      data-testid="guest-project-skeleton"
    >
      <span className="sr-only">
        {t('roadmapper.components.guest-project-skeleton.loading-shared-project')}
      </span>

      {/* Header bar: eyebrow, title, description, share terms, actions. */}
      <div className="border-b border-border bg-card">
        <div className="container mx-auto flex flex-col gap-5 px-6 py-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <Block className="h-4 w-4" />
              <Block className="h-3 w-20" />
              <Block className="h-3 w-24" />
            </div>
            <Block className="mt-3 h-8 w-2/3 sm:h-10" />
            <Block className="mt-2 h-4 w-full max-w-3xl" />
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Block className="h-3 w-24" />
              <Block className="h-3 w-32" />
              <Block className="h-3 w-28" />
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-1">
            <Block className="h-8 w-20" />
            <Block className="h-8 w-8" />
            <Block className="h-8 w-28" />
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-6">
        {/* Four scope cards. */}
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[0, 1, 2, 3].map(card => (
            <div
              key={card}
              className="rounded-lg border border-border bg-card p-4 shadow-[var(--shadow-surface-1)]"
            >
              <div className="flex items-center justify-between">
                <Block className="h-3 w-24" />
                <Block className="h-4 w-4" />
              </div>
              <Block className="mt-3 h-8 w-16" />
            </div>
          ))}
        </div>

        {/* The roadmaps section: toolbar above a timeline of rows. */}
        <div className="mt-6 rounded-lg border border-border bg-card p-4 shadow-[var(--shadow-surface-1)]">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Block className="h-5 w-40" />
            <div className="flex items-center gap-2">
              <Block className="h-8 w-48" />
              <Block className="h-8 w-24" />
            </div>
          </div>
          <div className="mt-4 space-y-2">
            {/*
              Offset and width vary per row so the wait reads as a timeline
              rather than a stack of identical bars — the shape of the wait
              matches the shape of the answer.
            */}
            {TIMELINE_BARS.map((bar, row) => (
              <div key={row} className="flex items-center gap-3">
                <Block className={`h-8 shrink-0 ${bar.labelWidthClass}`} />
                <div className="h-8 flex-1">
                  <div
                    className="h-8 rounded bg-muted"
                    style={{ marginLeft: bar.offset, width: bar.width }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
